/**
 * One place that holds a form conversion until the form says what came of it.
 *
 * Both submit paths need this and neither may own it, so it lives on its own:
 * dom-listeners.ts sees the native `submit` event, ajax-form-detection.ts sees
 * the click on the submit button, and a form framework fires whichever of the
 * two it feels like. Keeping the wait in one module is also the point: two
 * copies of a rule about when to count a conversion is exactly how the gate in
 * this codebase drifted apart before.
 *
 * What the observation on 6 August 2026 actually showed, after one wrong guess:
 *
 * - Contact Form 7 puts `novalidate` on its form and validates server-side, so
 *   the native `submit` event DOES fire on a submission that will be refused.
 *   Sending on `submit` therefore counted rejected submissions as leads.
 * - CF7 then answers with `wpcf7invalid`, roughly 170ms later. That event is
 *   the only trustworthy verdict, and it arrives well after the submit.
 *
 * So the rule is: a submit whose default was prevented is a framework taking
 * over, and its outcome is not known yet. Wait for the framework to say. A
 * submit that was NOT prevented navigates the browser away, which is a real
 * submission, and that one is reported at once.
 */

import { debugLog } from "./state.js";
import {
  decideSubmission,
  NATIVE_SUBMISSION_SIGNALS,
  JQUERY_SUBMISSION_SIGNALS,
  SIGNAL_WAIT_MS,
} from "./form-submission-outcome.js";
import type { SubmissionOutcome, ResolveReason } from "./form-submission-outcome.js";

/**
 * The conversion waiting on a verdict, as a closure.
 *
 * A closure rather than the event data, because the two callers build their
 * payload differently: one from a tracking rule, the other from data-tst-*
 * attributes on the form. What they share is the question of whether to send
 * at all.
 *
 * One at a time. A visitor submits one form, and a queue here would only keep
 * stale conversions alive across pages.
 */
let pendingSend: (() => void) | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

export function hasPendingSubmission(): boolean {
  return pendingSend !== null;
}

export function clearPendingSubmission(): void {
  if (pendingTimer !== null) clearTimeout(pendingTimer);
  pendingTimer = null;
  pendingSend = null;
}

/** Hold this conversion until the form's framework reports, or the wait ends. */
export function armPendingSubmission(send: () => void): void {
  clearPendingSubmission();
  pendingSend = send;
  pendingTimer = setTimeout(() => resolvePendingSubmission(null, "timeout"), SIGNAL_WAIT_MS);
  debugLog("pendingSubmission", "armed, waiting up to " + SIGNAL_WAIT_MS + "ms for a verdict");
}

export function resolvePendingSubmission(
  signal: SubmissionOutcome | null,
  reason: ResolveReason,
): void {
  const send = pendingSend;
  if (!send) return;
  clearPendingSubmission();

  const decision = decideSubmission({ blockedByValidation: false, signal, reason });
  debugLog("pendingSubmission", "resolved by " + reason + " -> " + decision);
  if (decision === "count") send();
}

/**
 * Listen for the outcome a form framework announces about itself.
 *
 * Two lists, because they are not reachable the same way. Contact Form 7
 * dispatches real DOM CustomEvents. WPForms, Gravity, Elementor, Formidable and
 * Ninja trigger theirs through jQuery, where a native addEventListener hears
 * nothing at all: listening for those natively would look like coverage while
 * catching nothing.
 */
export function installSubmissionSignalListeners(): void {
  for (const name of Object.keys(NATIVE_SUBMISSION_SIGNALS)) {
    document.addEventListener(name, () => {
      resolvePendingSubmission(NATIVE_SUBMISSION_SIGNALS[name], "signal");
    });
  }

  // A page that goes away while we are still waiting takes the answer with it.
  // Navigating away right after submitting is what a successful submission
  // usually looks like, so the held conversion is sent rather than dropped.
  window.addEventListener("pagehide", () => resolvePendingSubmission(null, "unload"));

  const jq = (window as Window & {
    jQuery?: (target: Document) => { on: (type: string, handler: () => void) => void };
  }).jQuery;
  if (!jq) {
    debugLog("pendingSubmission", "no jQuery — WPForms/Gravity/Elementor/Ninja/Formidable verdicts cannot be heard");
    return;
  }
  for (const name of Object.keys(JQUERY_SUBMISSION_SIGNALS)) {
    jq(document).on(name, () => {
      resolvePendingSubmission(JQUERY_SUBMISSION_SIGNALS[name], "signal");
    });
  }
}
